import React, { useEffect, useState } from "react";
import { Flower2, Plus, Trash2, Check, Droplets, CalendarClock, X } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { fmtDateTime } from "@/lib/format";
import { Card, Loading, EmptyState, PageHeader, Button, Modal, Badge, PhotoUpload } from "@/components/common";

function emptyVariant() {
  return { name: "", before_file_id: null, after_file_id: null, submerged_confirmed: false, note: "" };
}

export default function Care() {
  const { branch, isOwner } = useAuth();
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [flowers, setFlowers] = useState([]);
  const [logs, setLogs] = useState([]);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);

  const load = async () => {
    if (!branch) return;
    setLoading(true);
    try {
      const [st, tpl, fl, lg] = await Promise.all([
        api.get("/care/status", { branch_id: branch.id }),
        api.get("/care/templates"),
        api.get("/materials", { category: "fresh", branch_id: branch.id }).catch(() => []),
        api.get("/care/logs", { branch_id: branch.id }),
      ]);
      setStatus(st); setTemplates(tpl); setFlowers(fl); setLogs(lg);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [branch]);

  const del = async (id) => {
    if (!window.confirm("Hapus catatan perawatan ini?")) return;
    try { await api.delete(`/care/logs/${id}`); toast.success("Catatan dihapus"); load(); }
    catch (e) { toast.error(e.message); }
  };

  const setInterval = async () => {
    const v = window.prompt("Perawatan dijadwalkan setiap berapa hari?", String(status?.interval_days || 3));
    if (!v) return;
    try { const r = await api.patch("/care/config", { interval_days: parseInt(v, 10) }); toast.success(`Jadwal: tiap ${r.interval_days} hari`); load(); }
    catch (e) { toast.error(e.message); }
  };

  if (loading) return <Loading label="Memuat perawatan..." />;

  return (
    <>
      <PageHeader title="Perawatan Bunga" subtitle={branch?.name} icon={Flower2}
        actions={<div className="flex gap-2">
          {isOwner && <Button variant="secondary" onClick={setInterval} data-testid="care-interval-btn"><CalendarClock size={16} /> Tiap {status?.interval_days || 3} hari</Button>}
          <Button onClick={() => setCreating(true)} data-testid="care-add-btn"><Plus size={16} /> Isi Perawatan</Button>
        </div>} />

      {status && (
        <Card className={`mb-6 flex items-center gap-4 ${status.due ? "border-amber-300 bg-amber-50" : "border-emerald-200 bg-emerald-50"}`}>
          <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${status.due ? "bg-amber-500" : "bg-emerald-500"} text-white`}>
            {status.due ? <CalendarClock size={22} /> : <Check size={22} />}
          </div>
          <div className="flex-1">
            <div className="font-semibold text-slate-800" data-testid="care-status-title">
              {status.due ? "Perawatan jatuh tempo — mohon dikerjakan hari ini" : "Perawatan sudah dikerjakan sesuai jadwal"}
            </div>
            <div className="text-sm text-slate-500">
              {status.last_done_date ? `Terakhir: ${status.last_done_date}` : "Belum ada catatan"} · Jadwal berikutnya: {status.next_due_date} (tiap {status.interval_days} hari)
            </div>
          </div>
        </Card>
      )}

      <div className="text-sm font-semibold uppercase text-slate-400 mb-2">Riwayat Perawatan</div>
      {logs.length === 0 ? (
        <EmptyState icon={Flower2} title="Belum ada catatan perawatan" message="Mulai isi checklist perawatan bunga pertama Anda." />
      ) : (
        <div className="space-y-3">
          {logs.map((l) => (
            <Card key={l.id} className="flex items-start gap-4" data-testid={`care-log-${l.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-slate-800">{l.date}</span>
                  <Badge className="bg-primary/10 text-primary">{l.variant_count} varian</Badge>
                  <Badge className="bg-slate-100 text-slate-600">{l.tasks_done}/{l.tasks_total} tugas</Badge>
                </div>
                <div className="text-xs text-slate-400 mt-0.5">oleh {l.created_by_name} · {fmtDateTime(l.created_at)}</div>
                <div className="flex gap-2 mt-2 flex-wrap">
                  {l.variants.slice(0, 6).map((v, i) => (
                    <img key={i} src={fileUrl(v.after_file_id)} alt={v.name} title={v.name} className="w-12 h-12 rounded-lg object-cover border border-slate-200" />
                  ))}
                </div>
              </div>
              <div className="flex flex-col gap-2 items-end shrink-0">
                <Button variant="secondary" size="sm" onClick={() => setViewing(l)} data-testid={`care-view-${l.id}`}>Detail</Button>
                {isOwner && <Button variant="secondary" size="sm" onClick={() => setEditing(l)} data-testid={`care-edit-${l.id}`}>Edit</Button>}
                {isOwner && <button onClick={() => del(l.id)} className="text-xs text-rose-600 inline-flex items-center gap-1" data-testid={`care-del-${l.id}`}><Trash2 size={13} /> Hapus</button>}
              </div>
            </Card>
          ))}
        </div>
      )}

      {creating && <CareForm branch={branch} templates={templates} flowers={flowers} onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />}
      {editing && <CareForm branch={branch} templates={templates} flowers={flowers} editLog={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {viewing && <CareDetail log={viewing} onClose={() => setViewing(null)} />}
    </>
  );
}

function CareForm({ branch, templates, flowers, editLog, onClose, onSaved }) {
  const [tasks, setTasks] = useState(
    templates.map((tpl) => {
      const ex = editLog?.tasks?.find((t) => t.key === tpl.key);
      return { key: tpl.key, title: tpl.title, category: tpl.category, per_variant: tpl.per_variant, before_file_id: ex?.before_file_id || null, after_file_id: ex?.after_file_id || null, note: ex?.note || "" };
    })
  );
  const [variants, setVariants] = useState(editLog ? editLog.variants.map((v) => ({ ...v })) : [emptyVariant()]);
  const [notes, setNotes] = useState(editLog?.notes || "");
  const [busy, setBusy] = useState(false);

  const setTask = (i, patch) => setTasks((ts) => ts.map((t, idx) => idx === i ? { ...t, ...patch } : t));
  const setVar = (i, patch) => setVariants((vs) => vs.map((v, idx) => idx === i ? { ...v, ...patch } : v));
  const addVar = () => setVariants((vs) => [...vs, emptyVariant()]);
  const rmVar = (i) => setVariants((vs) => vs.filter((_, idx) => idx !== i));

  const submit = async () => {
    for (const t of tasks) {
      if (!t.before_file_id) return toast.error(`Foto Sebelum untuk tugas "${t.title}" wajib`);
      if (!t.after_file_id) return toast.error(`Foto Sesudah untuk tugas "${t.title}" wajib`);
    }
    const clean = variants.filter((v) => v.name.trim());
    if (clean.length === 0) return toast.error("Tambahkan minimal satu varian bunga");
    for (const v of clean) {
      if (!v.before_file_id) return toast.error(`Foto Sebelum untuk "${v.name}" wajib`);
      if (!v.after_file_id) return toast.error(`Foto Sesudah untuk "${v.name}" wajib`);
      if (!v.submerged_confirmed) return toast.error(`Centang konfirmasi tangkai "${v.name}" terendam air`);
    }
    setBusy(true);
    try {
      if (editLog) await api.patch(`/care/logs/${editLog.id}`, { tasks, variants: clean, notes });
      else await api.post("/care/logs", { branch_id: branch.id, tasks, variants: clean, notes });
      toast.success(editLog ? "Perawatan diperbarui" : "Perawatan tersimpan");
      onSaved();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <Modal open onClose={onClose} title={editLog ? "Edit Perawatan Bunga" : "Checklist Perawatan Bunga"} maxWidth="max-w-2xl">
      <div className="space-y-5">
        <div>
          <div className="text-sm font-semibold text-slate-700 mb-0.5">Tugas Perawatan (SOP)</div>
          <div className="text-xs text-slate-400 mb-2">Setiap tugas wajib foto Sebelum & Sesudah.</div>
          <div className="space-y-3">
            {tasks.map((t, i) => (
              <div key={t.key} className="rounded-xl border border-slate-200 p-3" data-testid={`care-task-${t.key}`}>
                <div className="flex items-center gap-2 mb-2">
                  <span className="font-medium text-slate-800 text-sm">{t.title}</span>
                  {t.per_variant && <Badge className="bg-primary/10 text-primary">bunga</Badge>}
                  {t.before_file_id && t.after_file_id && <Check size={16} className="text-emerald-500 ml-auto" />}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <PhotoUpload label="Foto Sebelum" value={t.before_file_id} onChange={(id) => setTask(i, { before_file_id: id })} testid={`care-task-before-${t.key}`} />
                  <PhotoUpload label="Foto Sesudah" value={t.after_file_id} onChange={(id) => setTask(i, { after_file_id: id })} testid={`care-task-after-${t.key}`} />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-semibold text-slate-700">Rincian per Varian Bunga</div>
            <Button variant="secondary" size="sm" onClick={addVar} data-testid="care-add-variant"><Plus size={14} /> Varian</Button>
          </div>
          <div className="text-xs text-slate-400 mb-3 flex items-center gap-1"><Droplets size={13} /> Foto Sesudah wajib memperlihatkan tangkai terendam air.</div>
          <datalist id="flower-options">{flowers.map((f) => <option key={f.id} value={f.name} />)}</datalist>
          <div className="space-y-4">
            {variants.map((v, i) => (
              <div key={i} className="rounded-xl border border-slate-200 p-3 space-y-3" data-testid={`care-variant-${i}`}>
                <div className="flex items-center gap-2">
                  <input list="flower-options" value={v.name} onChange={(e) => setVar(i, { name: e.target.value })} placeholder="Nama varian bunga (mis. Mawar Merah)"
                    className="flex-1 px-3 h-10 rounded-xl border border-slate-300 text-sm" data-testid={`care-variant-name-${i}`} />
                  {variants.length > 1 && <button type="button" onClick={() => rmVar(i)} className="w-9 h-9 rounded-lg text-rose-500 hover:bg-rose-50 flex items-center justify-center" data-testid={`care-variant-rm-${i}`}><X size={16} /></button>}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <PhotoUpload label="Foto Sebelum" value={v.before_file_id} onChange={(id) => setVar(i, { before_file_id: id })} testid={`care-before-${i}`} />
                  <PhotoUpload label="Foto Sesudah (tangkai di air)" value={v.after_file_id} onChange={(id) => setVar(i, { after_file_id: id })} testid={`care-after-${i}`} />
                </div>
                <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                  <input type="checkbox" checked={v.submerged_confirmed} onChange={(e) => setVar(i, { submerged_confirmed: e.target.checked })} data-testid={`care-submerged-${i}`} />
                  Saya konfirmasi tangkai & batang bunga ini terendam air pada Foto Sesudah
                </label>
              </div>
            ))}
          </div>
        </div>

        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Catatan / kendala (opsional)" rows={2} className="w-full px-3 py-2 rounded-xl border border-slate-300 text-sm" data-testid="care-notes" />

        <div className="flex gap-2 justify-end">
          <Button variant="secondary" onClick={onClose}>Batal</Button>
          <Button onClick={submit} disabled={busy} data-testid="care-submit">{busy ? "Menyimpan..." : editLog ? "Simpan Perubahan" : "Simpan Perawatan"}</Button>
        </div>
      </div>
    </Modal>
  );
}

function CareDetail({ log, onClose }) {
  return (
    <Modal open onClose={onClose} title={`Perawatan ${log.date}`} maxWidth="max-w-2xl">
      <div className="space-y-4">
        <div className="text-xs text-slate-400">oleh {log.created_by_name} · {fmtDateTime(log.created_at)}</div>
        <div>
          <div className="text-sm font-semibold text-slate-700 mb-2">Tugas & Foto Before / After</div>
          <div className="space-y-3">
            {log.tasks.map((t) => (
              <div key={t.key} className="rounded-xl border border-slate-200 p-3">
                <div className="flex items-center gap-2 mb-2">
                  <span className="font-medium text-slate-800 text-sm">{t.title}</span>
                  {t.per_variant && <Badge className="bg-primary/10 text-primary">bunga</Badge>}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><div className="text-xs text-slate-400 mb-1">Sebelum</div>{t.before_file_id ? <img src={fileUrl(t.before_file_id)} alt="" className="w-full h-32 object-cover rounded-lg border border-slate-200" /> : <span className="text-xs text-slate-300">-</span>}</div>
                  <div><div className="text-xs text-slate-400 mb-1">Sesudah</div>{t.after_file_id ? <img src={fileUrl(t.after_file_id)} alt="" className="w-full h-32 object-cover rounded-lg border border-slate-200" /> : <span className="text-xs text-slate-300">-</span>}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div>
          <div className="text-sm font-semibold text-slate-700 mb-2">Rincian per Varian Bunga</div>
          <div className="space-y-3">
            {log.variants.map((v, i) => (
              <div key={i} className="rounded-xl border border-slate-200 p-3">
                <div className="flex items-center gap-2 mb-2">
                  <span className="font-medium text-slate-800">{v.name}</span>
                  {v.submerged_confirmed && <Badge className="bg-sky-100 text-sky-700 flex items-center gap-1"><Droplets size={11} /> terendam air</Badge>}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><div className="text-xs text-slate-400 mb-1">Sebelum</div><img src={fileUrl(v.before_file_id)} alt="" className="w-full h-40 object-cover rounded-lg border border-slate-200" /></div>
                  <div><div className="text-xs text-slate-400 mb-1">Sesudah</div><img src={fileUrl(v.after_file_id)} alt="" className="w-full h-40 object-cover rounded-lg border border-slate-200" /></div>
                </div>
              </div>
            ))}
          </div>
        </div>
        {log.notes && <div className="text-sm text-slate-600"><span className="font-semibold">Catatan:</span> {log.notes}</div>}
      </div>
    </Modal>
  );
}
